import React, { useState, useEffect } from 'react';
import { api } from '../services/api';

interface User {
  id: number;
  username: string;
  avatar_url: string | null;
}

interface CreateGroupModalProps {
  onClose: () => void;
  onCreated: () => void;
}

const CreateGroupModal: React.FC<CreateGroupModalProps> = ({ onClose, onCreated }) => {
  const [name, setName] = useState('');
  const [search, setSearch] = useState('');
  const [users, setUsers] = useState<User[]>([]);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (search.length < 2) { setUsers([]); return; }
    const timer = setTimeout(async () => {
      try {
        const res = await api.get(`/messages/search/${encodeURIComponent(search)}`);
        setUsers(res.data);
      } catch {}
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  const toggle = (id: number) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const create = async () => {
    if (!name.trim() || selected.size === 0) return;
    setLoading(true);
    try {
      await api.post('/messages/group/create', {
        name: name.trim(),
        member_ids: Array.from(selected)
      });
      onCreated();
    } catch {}
    setLoading(false);
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#1a1d24] border border-gray-700 rounded-2xl w-full max-w-md" onClick={e => e.stopPropagation()}>
        <div className="p-4 border-b border-gray-700 flex items-center justify-between">
          <h3 className="text-white font-semibold">Новый групповой чат</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white text-xl">×</button>
        </div>

        <div className="p-4 space-y-3">
          <input
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder="Название группы"
            className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white placeholder-gray-500 text-sm focus:outline-none focus:border-blue-500"
          />

          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Найти пользователей..."
            className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white placeholder-gray-500 text-sm focus:outline-none focus:border-blue-500"
          />

          {selected.size > 0 && (
            <div className="flex flex-wrap gap-1">
              {Array.from(selected).map(id => {
                const u = users.find(x => x.id === id);
                return (
                  <span key={id} className="inline-flex items-center gap-1 bg-blue-500/20 text-blue-400 text-xs px-2 py-0.5 rounded-full">
                    {u?.username || id}
                    <button onClick={() => toggle(id)} className="hover:text-white">×</button>
                  </span>
                );
              })}
            </div>
          )}

          {users.length > 0 && (
            <div className="max-h-48 overflow-y-auto space-y-1">
              {users.map(u => (
                <button
                  key={u.id}
                  onClick={() => toggle(u.id)}
                  className={`w-full flex items-center gap-3 p-2 rounded-lg transition text-left ${
                    selected.has(u.id) ? 'bg-blue-500/10 border border-blue-500/30' : 'hover:bg-gray-800 border border-transparent'
                  }`}
                >
                  <div className="w-8 h-8 rounded-full bg-gray-700 overflow-hidden flex-shrink-0">
                    {u.avatar_url ? <img src={u.avatar_url} className="w-full h-full object-cover" /> : <div className="w-full h-full" />}
                  </div>
                  <span className="text-white text-sm">{u.username}</span>
                  {selected.has(u.id) && <span className="ml-auto text-blue-400 text-xs">✓</span>}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="p-4 border-t border-gray-700 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-gray-400 hover:text-white text-sm transition">Отмена</button>
          <button
            onClick={create}
            disabled={loading || !name.trim() || selected.size === 0}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg text-sm transition"
          >
            {loading ? 'Создание...' : 'Создать'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateGroupModal;
